import { Controller } from "@hotwired/stimulus"
import { hideIntercom, showIntercom } from "../helpers/intercom"
import { confirmAction } from "../lib/confirm"
import { canSignWithoutConsultant, setSkipConsultantMode, withSkipConsultant } from "../lib/sign_without_consultant"

const SVG_PENCIL_SQUARE = '<svg class="w-3.5 h-3.5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10"/></svg>'
const SVG_CHECK = '<svg class="w-3.5 h-3.5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="m4.5 12.75 6 6 9-13.5"/></svg>'
const SVG_INFO_CIRCLE = '<svg class="w-3.5 h-3.5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="m11.25 11.25.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z"/></svg>'

// Pay request backup files: PDFs, plus photos — the PDF gives each photo a
// page of its own (PdfMerger.append_file; Larry Daleo, Intercom 2026-11-03).
const PR_UPLOAD_EXTENSIONS = ["pdf", "jpg", "jpeg", "png", "gif", "heic", "heif", "webp", "tiff", "bmp"]
const prUploadable = f => PR_UPLOAD_EXTENSIONS.includes((f.name.split(".").pop() || "").toLowerCase())
const prContentType = f => f.type || (f.name.toLowerCase().endsWith(".pdf") ? "application/pdf" : "application/octet-stream")

export default class extends Controller {
  static targets = [
    "newPrModal", "modalTitle",
    "prProjectNumber", "prPo", "prVendorId",
    "prInvoiceNumber",
    "prLineItems", "prContractTotal", "prPcocTotal", "prCoTotal", "prAcaTotal", "prPrevTotal", "prThisTotal", "prCompletedTotal", "prPercentTotal", "prBalanceTotal", "prRetentionTotal", "retentionHeader",
    "prPrevRetention", "prThisAppRetention", "prRetentionBalance", "prPrevRetentionToPay", "prRetentionToPay", "prRetentionPaidBalance",
    "prLessLiquidatedDamages", "prOtherDeductions", "prOtherAdditions", "prTaxes", "prNetPayment",
    "deleteModal", "signModal", "unsignModal", "requestApprovalModal",
    "modalSkipConsultantBtn", "modalConsultantSkipped", "skipConsultantNote", "signTitle", "signConfirmBtn",
    "prDropzone", "prFileInput", "prFileList", "prPickPhotos", "photoPicker",
    "addPastPrBtn", "saveDraftBtn", "signRequestBtn",
    "pastPrConfirmModal", "pastPrSequenceLabel",
    "prHistoryLog", "prHistoryLogEntries",
    "modalRequestApprovalBtn", "modalSignedBadge", "modalSignedText", "modalSignedTooltip", "modalSignedCheck",
    "unsignBtn", "modalPendingLabel", "modalPendingConsultantLabel", "modalDeleteBtn", "modalEditPastBtn", "modalApproveBtn", "modalRejectBtn",
    "rejectPrModal", "rejectComment",
    "overallTotals", "summaryOriginalContract", "summaryCoTotals", "summaryCoSymbol", "summaryAca", "summaryCompleted", "summaryPercent", "summaryBalance", "summaryRetentionBalance",
    "prCards", "emptyState",
    "uploadSection", "uploadLabel",
    "generalInfoModal",
    "giBuildingType", "giStakeMission", "giStartDate", "giEndDate",
    "giProjectNumber", "giContractDays",
    "giContractorCompanyId",
    "giVendorId", "giPoNumber",
    "ntpSection", "ntpDateInput", "ntpError",
    "renumberWrap", "renumberSelect"
  ]

  static values = {
    projectId: Number,
    currency: String,
    currencyFormat: String,
    delimiter: String,
    separator: String,
    isOwner: Boolean,
    // PM Permissions: stakeholder granted owner-side pay-request approval.
    // Gates only the approve/reject/unsign side — never edit/delete powers.
    canApprove: Boolean,
    // Approval Settings → "Allow Owners To Sign Without The Consultant".
    skipConsultantAllowed: Boolean,
    isContractor: Boolean,
    isConsultant: Boolean,
    sovApproved: Boolean,
    retainage: Number,
    projectStartedAt: String,
    requirePrAttachment: Boolean,
    ownerCompanyId: Number,
    // Legacy projects whose retention lands on a half cent (project 7976,
    // Phelipe 2026-10-07): the balance cell and the Retention column total
    // round it the way the summary does, so the web and the PDF agree
    // (FinancesController::RETENTION_ONE_CENT_PROJECT_IDS).
    retentionOneCent: Boolean
  }

  connect() {
    this._csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    const pendingFlash = sessionStorage.getItem("prFlashMessage")
    if (pendingFlash) {
      sessionStorage.removeItem("prFlashMessage")
      this._showToast(pendingFlash)
    }
    this._editSequence = null
    this._isPast = false
    this._pendingDeleteSequence = null
    this._pendingSignSequence = null
    this._lineItemsData = []
    this._attachmentIds = []
    this._pendingUploads = 0
    this._previousPrTotals = {}
    this._signFromEditModal = false
    this._signFromEditIsNew = false
    this._signFromEditSaveResp = null
    this._approveAfterAcknowledge = false
    this._pdfPollingTimers = {}
  }

  disconnect() {
    Object.values(this._pdfPollingTimers).forEach(t => clearTimeout(t))
    this._pdfPollingTimers = {}
  }

  // ── Currency helpers ──────────────────────────────────────────────

  _parseCurrency(str) {
    if (!str) return 0
    let cleaned = str.replace(this.currencyValue, "").trim()
    let neg = false
    if (cleaned.startsWith("(") && cleaned.endsWith(")")) {
      neg = true
      cleaned = cleaned.slice(1, -1)
    }
    if (this.currencyFormatValue === "european_currency_format") {
      cleaned = cleaned.replace(/\./g, "").replace(",", ".")
    } else if (this.currencyFormatValue === "swiss_currency_format") {
      cleaned = cleaned.replace(/'/g, "")
    } else {
      cleaned = cleaned.replace(/,/g, "")
    }
    const val = parseFloat(cleaned) || 0
    return neg ? -val : val
  }

  _formatCurrency(num) {
    if (num === null || num === undefined || isNaN(num)) return ""
    const abs = Math.abs(num)
    let formatted
    if (this.currencyFormatValue === "european_currency_format") {
      formatted = abs.toFixed(2).replace(".", ",").replace(/\B(?=(\d{3})+(?!\d))/g, ".")
    } else if (this.currencyFormatValue === "swiss_currency_format") {
      formatted = abs.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, "'")
    } else {
      formatted = abs.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")
    }
    if (num < 0) return `${this.currencyValue}(${formatted})`
    return `${this.currencyValue}${formatted}`
  }

  _formatAmount(num, precision = 2) {
    if (num === null || num === undefined || isNaN(num)) return ""
    const abs = Math.abs(num)
    let formatted
    if (this.currencyFormatValue === "european_currency_format") {
      formatted = abs.toFixed(precision).replace(".", ",").replace(/\B(?=(\d{3})+(?!\d))/g, ".")
    } else if (this.currencyFormatValue === "swiss_currency_format") {
      formatted = abs.toFixed(precision).replace(/\B(?=(\d{3})+(?!\d))/g, "'")
    } else {
      formatted = abs.toFixed(precision).replace(/\B(?=(\d{3})+(?!\d))/g, ",")
    }
    if (num < 0) return `(${formatted})`
    return formatted
  }

  _escAttr(str) {
    return String(str).replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
  }

  // ── Required signatures helper ────────────────────────────────────

  get _requiredSigs() {
    // Default 3 (contractor + consultant + owner), 2 if consultant waived
    const card = this._editSequence
      ? this.element.querySelector(`.payment-request[data-sequence="${this._editSequence}"]`)
      : null
    if (card && card.dataset.requiredSigs) {
      return parseInt(card.dataset.requiredSigs) || 3
    }
    return this._currentRequiredSigs || 3
  }

  // ── New PR ─────────────────────────────────────────────────────

  async newPaymentRequest() {
    if (!this.sovApprovedValue) {
      this._showToast("SOV must be approved before creating a payment request.", "alert")
      return
    }
    this._editSequence = null
    this._isPast = false
    this.modalTitleTarget.textContent = "New Payment Request"
    await this._loadLineItemsAndShowModal()
  }

  async newPaidPaymentRequest() {
    if (!this.sovApprovedValue) {
      this._showToast("SOV must be approved before creating a payment request.", "alert")
      return
    }
    this._editSequence = null
    this._isPast = true
    this.modalTitleTarget.textContent = "Past Payment Request"
    await this._loadLineItemsAndShowModal()
  }

  async editPaymentRequest(event) {
    this._hideRenumberSelect()
    const sequence = event.currentTarget.dataset.sequence
    this._editSequence = sequence
    const card = this.element.querySelector(`.payment-request[data-sequence="${sequence}"]`)
    this._isPast = card?.dataset.isPast === "true"
    this.modalTitleTarget.textContent = this._isPast ? `Past Payment Request #${sequence}` : `Payment Request #${sequence}`
    await this._loadLineItemsAndShowModal(sequence)
  }

  async _loadLineItemsAndShowModal(sequence = null) {
    const url = `/projects/${this.projectIdValue}/finances/view_edit_pr_modal_data${sequence ? `?sequence=${sequence}` : ""}`
    const resp = await fetch(url, { headers: { "Accept": "application/json" } })
    if (!resp.ok) {
      const err = await resp.json().catch(() => ({}))
      this._showToast(err.error || "An error occurred.", "alert")
      return
    }
    const data = await resp.json()

    this._lineItemsData = data.sov_line_items
    this._coTotals = data.co_totals || {}
    this._pendingCoCredits = data.pending_co_credits || {}
    this._previousPrTotals = data.previous_pr_totals || {}
    this._attachmentIds = []
    this._pendingUploads = 0
    this._currentRequiredSigs = data.required_sigs || 3

    // Populate invoice fields
    if (this.hasPrInvoiceNumberTarget) this.prInvoiceNumberTarget.value = data.pr_data?.invoice_number || ""
    if (this.hasPrProjectNumberTarget) this.prProjectNumberTarget.value = data.project_number || this.prProjectNumberTarget.value || ""
    if (this.hasPrPoTarget) this.prPoTarget.value = data.po_number || this.prPoTarget.value || ""
    if (this.hasPrVendorIdTarget) this.prVendorIdTarget.value = data.vendor_id || this.prVendorIdTarget.value || ""

    // Populate retention & adjustments
    this._prevRetention = parseFloat(data.previous_retention) || 0
    this._prevRetentionToPay = parseFloat(data.previous_retention_to_pay) || 0
    this._retainageRate = data.pr_data?.retention_rate != null ? data.pr_data.retention_rate : (data.retainage || this.retainageValue || 0)
    if (this.hasRetentionHeaderTarget) {
      const rate = parseFloat(this._retainageRate) || 0
      this.retentionHeaderTarget.textContent = `Retention (${rate.toFixed(1)}%)`
    }
    if (this.hasPrPrevRetentionTarget) this.prPrevRetentionTarget.textContent = this._prevRetention ? this.currencyValue + this._formatAmount(this._prevRetention) : ""
    if (this.hasPrPrevRetentionToPayTarget) this.prPrevRetentionToPayTarget.textContent = this._prevRetentionToPay ? this.currencyValue + this._formatAmount(this._prevRetentionToPay) : ""
    if (this.hasPrRetentionToPayTarget) this.prRetentionToPayTarget.value = data.pr_data?.retention_to_pay ? this._formatAmount(data.pr_data.retention_to_pay) : ""
    if (this.hasPrLessLiquidatedDamagesTarget) this.prLessLiquidatedDamagesTarget.value = data.pr_data?.less_liquidated_damages ? this._formatAmount(data.pr_data.less_liquidated_damages) : ""
    if (this.hasPrOtherDeductionsTarget) this.prOtherDeductionsTarget.value = data.pr_data?.other_deductions ? this._formatAmount(data.pr_data.other_deductions) : ""
    if (this.hasPrOtherAdditionsTarget) this.prOtherAdditionsTarget.value = data.pr_data?.other_additions ? this._formatAmount(data.pr_data.other_additions) : ""
    if (this.hasPrTaxesTarget) this.prTaxesTarget.value = data.pr_data?.taxes ? this._formatAmount(data.pr_data.taxes) : ""
    // Fee data for Contractor's Fee row
    this._feeData = {
      scheduledValue: parseFloat(data.contractor_fee) || 0,
      coTotals: parseFloat(this._coTotals["fee"]) || 0,
      pcoc: parseFloat(this._pendingCoCredits["fee"]) || 0,
      previousApps: parseFloat(data.previous_fee_total) || 0,
      currentApp: parseFloat(data.pr_data?.contractor_fee) || 0
    }

    // Clear file list
    if (this.hasPrFileListTarget) this.prFileListTarget.innerHTML = ""

    this._renderPrLineItems(data.sov_line_items, data.pr_data?.items || [], this._previousPrTotals, this._coTotals, this._pendingCoCredits)

    // Load existing attachments when editing
    if (data.attachments && data.attachments.length > 0) {
      data.attachments.forEach(att => {
        this._attachmentIds.push(att.id)
        const chip = this._createFileChip(att.filename, {
          removable: true,
          url: att.url,
          onRemove: () => {
            chip.remove()
            this._attachmentIds = this._attachmentIds.filter(id => id !== att.id)
          }
        })
        if (this.hasPrFileListTarget) this.prFileListTarget.appendChild(chip)
      })
    }

    // Render history log
    this._renderHistoryLog(data.history_log || [])

    // Render modal header badges
    this._renderModalHeaderBadges(data)

    // Show/hide Add Past PR button
    if (this.hasAddPastPrBtnTarget) {
      this.addPastPrBtnTarget.classList.toggle("hidden", !!this._editSequence)
    }

    this.newPrModalTarget.classList.remove("hidden")
    hideIntercom()
  }

  _renderPrLineItems(lineItems, existingItems, previousPrTotals, coTotalsMap = {}, pendingCoCreditsMap = {}) {
    const existing = {}
    existingItems.forEach(item => { existing[item.line_item_id] = item })
    const cur = this.currencyValue
    const retRate = this._retainageRate || this.retainageValue || 0

    let html = ""
    const sorted = [...lineItems].sort((a, b) => {
      const ai = parseInt(a.item), bi = parseInt(b.item)
      if (isNaN(ai) && isNaN(bi)) return 0
      if (isNaN(ai)) return 1
      if (isNaN(bi)) return -1
      return ai - bi
    })
    sorted.forEach(li => {
      const ex = existing[li.id]
      const contract = parseFloat(li.scheduled_amount) || 0
      const pcoc = parseFloat(pendingCoCreditsMap[li.id]) || 0
      const coTotals = parseFloat(coTotalsMap[li.id]) || 0
      // Only show line items with scheduled value > 0 or CO totals > 0 (matches old app)
      if (contract <= 0 && coTotals <= 0 && pcoc >= 0) return
      const aca = contract + coTotals + pcoc
      const prevPr = parseFloat(previousPrTotals[li.id]) || 0
      const currentAmount = ex ? (parseFloat(ex.amount) || 0) : 0
      const completedToDate = prevPr + currentAmount
      const pctCompleted = aca > 0 ? ((completedToDate / aca) * 100).toFixed(0) + "%" : ""
      const balance = Math.round((aca - completedToDate) * 100) / 100
      const retention = completedToDate * retRate / 100

      const C = (val, field, extra = "") => `<td class="px-0 py-0 border-r border-gray-200"${extra} ${field ? `data-field="${field}"` : ""}><div class="flex items-center"><span class="pl-2 text-gray-400 select-none text-[12px]" data-sym="${field}">${val ? cur : ""}</span><span class="w-full px-1 py-1 pr-2 text-right text-[12px]">${val}</span></div></td>`

      const thisAppVal = ex ? this._formatAmount(currentAmount) : ""

      html += `
        <tr data-sov-line-item-id="${li.id}">
          <td class="px-2 py-1 border-r border-gray-200 text-[12px] truncate">${li.item || ""}</td>
          <td class="px-2 py-1 border-r border-gray-200 text-[12px] truncate">${li.csi_div || ""}</td>
          <td class="px-2 py-1 border-r border-gray-200 text-[12px] overflow-hidden"><div class="flex items-center justify-between gap-1"><span class="truncate">${li.description || ""}</span>${li.definition ? `<span class="shrink-0 cursor-help text-gray-400" data-tip="${this._escAttr(li.definition)}" data-tip-pos="left"><svg xmlns="http://www.w3.org/2000/svg" class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg></span>` : ""}</div></td>
          ${C(contract ? this._formatAmount(contract) : "", "contract", ` data-contract="${contract}"`)}
          <td class="px-0 py-0 border-r border-gray-200" data-field="pcoc" data-pcoc="${pcoc}"><div class="flex items-center"><span class="pl-2 text-red-600 select-none text-[12px]" data-sym="pcoc">${pcoc ? cur : ""}</span><span class="w-full px-1 py-1 pr-2 text-right text-[12px] text-red-600">${pcoc ? this._formatAmount(pcoc) : ""}</span></div></td>
          <td class="px-0 py-0 border-r border-gray-200" data-field="co-totals" data-co-totals="${coTotals}"><div class="flex items-center"><span class="pl-2 ${coTotals < 0 ? "text-red-600" : "text-gray-400"} select-none text-[12px]" data-sym="co-totals">${coTotals ? cur : ""}</span><span class="w-full px-1 py-1 pr-2 text-right text-[12px] ${coTotals < 0 ? "text-red-600" : ""}">${coTotals ? this._formatAmount(coTotals) : ""}</span></div></td>
          ${C(aca ? this._formatAmount(aca) : "", "aca")}
          <td class="px-0 py-0 border-r border-gray-200" data-field="prev-pr" data-prev-total="${prevPr}"><div class="flex items-center"><span class="pl-2 text-gray-400 select-none text-[12px]" data-sym="prev-pr">${prevPr ? cur : ""}</span><span class="w-full px-1 py-1 pr-2 text-right text-[12px]">${prevPr ? this._formatAmount(prevPr) : ""}</span></div></td>
          <td class="px-0 py-0 border-r border-gray-200 bg-amber-50">
            <div class="flex items-center">
              <span class="pl-2 text-gray-400 select-none text-[12px]" data-sym="this_application">${cur}</span>
              <input type="text" value="${thisAppVal}" data-field="this_application"
                data-action="input->contractor-pay-request#recalcPrTotals"
                class="w-full px-1 py-1 pr-2 text-right text-[12px] bg-transparent focus:outline-none">
            </div>
          </td>
          ${C(completedToDate ? this._formatAmount(completedToDate) : "", "completed-to-date")}
          <td class="px-2 py-1 text-center border-r border-gray-200 text-[12px]" data-field="pct-completed">${pctCompleted}</td>
          <td class="px-0 py-0 border-r border-gray-200" data-field="balance"><div class="flex items-center"><span class="pl-2 ${balance < 0 ? "text-red-600" : "text-gray-400"} select-none text-[12px]" data-sym="balance">${cur}</span><span class="w-full px-1 py-1 pr-2 text-right text-[12px] ${balance < 0 ? "text-red-600" : ""}">${this._formatAmount(balance)}</span></div></td>
          <td class="px-0 py-0" data-field="retention"><div class="flex items-center"><span class="pl-2 text-gray-400 select-none text-[12px]" data-sym="retention">${retention ? cur : ""}</span><span class="w-full px-1 py-1 pr-2 text-right text-[12px]">${retention ? this._formatAmount(retention, 3) : ""}</span></div></td>
        </tr>`
    })

    // Contractor's Fee row (rendered as a regular line item)
    const fee = this._feeData
    if (fee && (fee.scheduledValue > 0 || fee.coTotals !== 0 || fee.pcoc !== 0)) {
      const feeContract = fee.scheduledValue
      const feePcoc = fee.pcoc
      const feeCoTotals = fee.coTotals
      const feeAca = feeContract + feeCoTotals + feePcoc
      const feePrev = fee.previousApps
      const feeCurrent = fee.currentApp
      const feeCompleted = feePrev + feeCurrent
      const feePct = feeAca > 0 ? ((feeCompleted / feeAca) * 100).toFixed(0) + "%" : ""
      const feeBalance = Math.round((feeAca - feeCompleted) * 100) / 100
      const feeRetention = feeCompleted * retRate / 100
      const feeThisVal = feeCurrent ? this._formatAmount(feeCurrent) : ""
      const feeItemNum = lineItems.length + 1

      const CF = (val, field, extra = "") => `<td class="px-0 py-0 border-r border-gray-200"${extra} ${field ? `data-field="${field}"` : ""}><div class="flex items-center"><span class="pl-2 text-gray-400 select-none text-[12px]" data-sym="${field}">${val ? cur : ""}</span><span class="w-full px-1 py-1 pr-2 text-right text-[12px]">${val}</span></div></td>`

      html += `
        <tr data-sov-line-item-id="fee" data-is-fee="true">
          <td class="px-2 py-1 border-r border-gray-200 text-[12px] truncate">${feeItemNum}</td>
          <td class="px-2 py-1 border-r border-gray-200 text-[12px] truncate"></td>
          <td class="px-2 py-1 border-r border-gray-200 text-[12px] truncate max-w-[200px]">Contractor's Fee</td>
          ${CF(feeContract ? this._formatAmount(feeContract) : "", "contract", ` data-contract="${feeContract}"`)}
          <td class="px-0 py-0 border-r border-gray-200" data-field="pcoc" data-pcoc="${feePcoc}"><div class="flex items-center"><span class="pl-2 text-red-600 select-none text-[12px]" data-sym="pcoc">${feePcoc ? cur : ""}</span><span class="w-full px-1 py-1 pr-2 text-right text-[12px] text-red-600">${feePcoc ? this._formatAmount(feePcoc) : ""}</span></div></td>
          <td class="px-0 py-0 border-r border-gray-200" data-field="co-totals" data-co-totals="${feeCoTotals}"><div class="flex items-center"><span class="pl-2 ${feeCoTotals < 0 ? "text-red-600" : "text-gray-400"} select-none text-[12px]" data-sym="co-totals">${feeCoTotals ? cur : ""}</span><span class="w-full px-1 py-1 pr-2 text-right text-[12px] ${feeCoTotals < 0 ? "text-red-600" : ""}">${feeCoTotals ? this._formatAmount(feeCoTotals) : ""}</span></div></td>
          ${CF(feeAca ? this._formatAmount(feeAca) : "", "aca")}
          <td class="px-0 py-0 border-r border-gray-200" data-field="prev-pr" data-prev-total="${feePrev}"><div class="flex items-center"><span class="pl-2 text-gray-400 select-none text-[12px]" data-sym="prev-pr">${feePrev ? cur : ""}</span><span class="w-full px-1 py-1 pr-2 text-right text-[12px]">${feePrev ? this._formatAmount(feePrev) : ""}</span></div></td>
          <td class="px-0 py-0 border-r border-gray-200 bg-amber-50">
            <div class="flex items-center">
              <span class="pl-2 text-gray-400 select-none text-[12px]" data-sym="this_application">${cur}</span>
              <input type="text" value="${feeThisVal}" data-field="this_application"
                data-action="input->contractor-pay-request#recalcPrTotals"
                class="w-full px-1 py-1 pr-2 text-right text-[12px] bg-transparent focus:outline-none">
            </div>
          </td>
          ${CF(feeCompleted ? this._formatAmount(feeCompleted) : "", "completed-to-date")}
          <td class="px-2 py-1 text-center border-r border-gray-200 text-[12px]" data-field="pct-completed">${feePct}</td>
          <td class="px-0 py-0 border-r border-gray-200" data-field="balance"><div class="flex items-center"><span class="pl-2 ${feeBalance < 0 ? "text-red-600" : "text-gray-400"} select-none text-[12px]" data-sym="balance">${cur}</span><span class="w-full px-1 py-1 pr-2 text-right text-[12px] ${feeBalance < 0 ? "text-red-600" : ""}">${this._formatAmount(feeBalance)}</span></div></td>
          <td class="px-0 py-0" data-field="retention"><div class="flex items-center"><span class="pl-2 text-gray-400 select-none text-[12px]" data-sym="retention">${feeRetention ? cur : ""}</span><span class="w-full px-1 py-1 pr-2 text-right text-[12px]">${feeRetention ? this._formatAmount(feeRetention, 3) : ""}</span></div></td>
        </tr>`
    }

    this.prLineItemsTarget.innerHTML = html
    this._recalcFooterTotals()
  }

  // ── Calculation ───────────────────────────────────────────────────

  recalcPrTotals() {
    const retRate = this._retainageRate || this.retainageValue || 0
    // Update per-row computed columns
    this.prLineItemsTarget.querySelectorAll("tr").forEach(row => {
      const contractCell = row.querySelector("[data-field='contract']")
      const coCell = row.querySelector("[data-field='co-totals']")
      const pcocCell = row.querySelector("[data-field='pcoc']")
      const contract = parseFloat(contractCell?.dataset.contract) || 0
      const coTotals = parseFloat(coCell?.dataset.coTotals) || 0
      const pcoc = parseFloat(pcocCell?.dataset.pcoc) || 0
      const aca = contract + coTotals + pcoc
      const prevPr = parseFloat(row.querySelector("[data-field='prev-pr']")?.dataset.prevTotal) || 0
      const thisApp = this._parseCurrency(row.querySelector("[data-field='this_application']")?.value)
      const completedToDate = prevPr + thisApp
      const pctCompleted = aca > 0 ? ((completedToDate / aca) * 100).toFixed(0) + "%" : ""
      const balance = Math.round((aca - completedToDate) * 100) / 100
      const retention = completedToDate * retRate / 100

      // Update ACA
      const acaSpan = row.querySelector("[data-field='aca'] span:last-child")
      if (acaSpan) acaSpan.textContent = aca ? this._formatAmount(aca) : ""
      const acaSym = row.querySelector("[data-sym='aca']")
      if (acaSym) acaSym.textContent = aca ? this.currencyValue : ""

      const ctdSpan = row.querySelector("[data-field='completed-to-date'] span:last-child")
      if (ctdSpan) ctdSpan.textContent = completedToDate ? this._formatAmount(completedToDate) : ""
      const ctdSym = row.querySelector("[data-sym='completed-to-date']")
      if (ctdSym) ctdSym.textContent = completedToDate ? this.currencyValue : ""

      const pctEl = row.querySelector("[data-field='pct-completed']")
      if (pctEl) pctEl.textContent = pctCompleted

      const balSpan = row.querySelector("[data-field='balance'] span:last-child")
      if (balSpan) {
        balSpan.textContent = this._formatAmount(balance)
        balSpan.classList.toggle("text-red-600", balance < 0)
      }
      const balSym = row.querySelector("[data-sym='balance']")
      if (balSym) {
        balSym.textContent = this.currencyValue
        balSym.classList.toggle("text-red-600", balance < 0)
        balSym.classList.toggle("text-gray-400", balance >= 0)
      }

      const retSpan = row.querySelector("[data-field='retention'] span:last-child")
      if (retSpan) retSpan.textContent = retention ? this._formatAmount(retention, 3) : ""
      const retSym = row.querySelector("[data-sym='retention']")
      if (retSym) retSym.textContent = retention ? this.currencyValue : ""
    })
    this._recalcFooterTotals()
  }

  _recalcFooterTotals() {
    let contractTotal = 0, pcocTotal = 0, coTotal = 0, acaTotal = 0, prevTotal = 0, thisTotal = 0, completedTotal = 0, balanceTotal = 0, retentionTotal = 0

    this.prLineItemsTarget.querySelectorAll("tr").forEach(row => {
      const contractCell = row.querySelector("[data-field='contract']")
      const coCell = row.querySelector("[data-field='co-totals']")
      const pcocCell = row.querySelector("[data-field='pcoc']")
      contractTotal += parseFloat(contractCell?.dataset.contract) || 0
      pcocTotal += parseFloat(pcocCell?.dataset.pcoc) || 0
      coTotal += parseFloat(coCell?.dataset.coTotals) || 0
      acaTotal += this._parseCurrency(row.querySelector("[data-field='aca']")?.textContent)
      prevTotal += parseFloat(row.querySelector("[data-field='prev-pr']")?.dataset.prevTotal) || 0
      thisTotal += this._parseCurrency(row.querySelector("[data-field='this_application']")?.value)
      completedTotal += this._parseCurrency(row.querySelector("[data-field='completed-to-date']")?.textContent)
      balanceTotal += this._parseCurrency(row.querySelector("[data-field='balance']")?.textContent)
      retentionTotal += this._parseCurrency(row.querySelector("[data-field='retention']")?.textContent)
    })

    const thisAppTotal = thisTotal

    const pctTotal = acaTotal > 0 ? ((completedTotal / acaTotal) * 100).toFixed(1) + "%" : "0.0%"

    const setFooter = (target, hasTarget, val) => { if (hasTarget) { const s = target.querySelector("span:last-child"); if (s) s.textContent = val } }
    setFooter(this.prContractTotalTarget, this.hasPrContractTotalTarget, this._formatAmount(contractTotal))
    setFooter(this.prPcocTotalTarget, this.hasPrPcocTotalTarget, this._formatAmount(pcocTotal))
    setFooter(this.prCoTotalTarget, this.hasPrCoTotalTarget, this._formatAmount(coTotal))
    if (this.hasPrCoTotalTarget) { const el = this.prCoTotalTarget; el.classList.toggle("text-red-600", coTotal < 0); const sym = el.querySelector("[data-sym]"); if (sym) { sym.classList.toggle("text-red-600", coTotal < 0); sym.classList.toggle("text-gray-400", coTotal >= 0) } }
    setFooter(this.prAcaTotalTarget, this.hasPrAcaTotalTarget, this._formatAmount(acaTotal))
    setFooter(this.prPrevTotalTarget, this.hasPrPrevTotalTarget, this._formatAmount(prevTotal))
    setFooter(this.prThisTotalTarget, this.hasPrThisTotalTarget, this._formatAmount(thisAppTotal))
    setFooter(this.prCompletedTotalTarget, this.hasPrCompletedTotalTarget, this._formatAmount(completedTotal))
    if (this.hasPrPercentTotalTarget) this.prPercentTotalTarget.textContent = pctTotal
    setFooter(this.prBalanceTotalTarget, this.hasPrBalanceTotalTarget, this._formatAmount(balanceTotal))
    // Project 7976 (Phelipe 2026-10-07): adding ~70 three-decimal line
    // retentions lands a hair under the half cent and printed .08, while the
    // summary's balance (rate x the total, rounded) says .09. Same figure, one
    // rounding: the column total is the rate x completed-to-date total.
    if (this.retentionOneCentValue) {
      const rate = this._retainageRate || this.retainageValue || 0
      retentionTotal = Math.round(completedTotal * (rate / 100) * 100) / 100
    }
    setFooter(this.prRetentionTotalTarget, this.hasPrRetentionTotalTarget, this._formatAmount(retentionTotal))

    // Retention & adjustments
    this._recalcRetentionAndAdjustments(thisAppTotal)
  }

  _recalcRetentionAndAdjustments(thisAppTotal) {
    const retentionRate = this._retainageRate || this.retainageValue || 0
    const prevRetention = this._prevRetention || 0
    const thisRetentionRaw = thisAppTotal * (retentionRate / 100)
    const prevRetentionToPay = this._prevRetentionToPay || 0

    // Retention row: derive This Application = round(Balance) - round(Previous) so columns reconcile
    const retBalance = Math.round((prevRetention + thisRetentionRaw) * 100) / 100
    const prevRetentionRounded = Math.round(prevRetention * 100) / 100
    const thisRetention = retBalance - prevRetentionRounded

    if (this.hasPrThisAppRetentionTarget) {
      this.prThisAppRetentionTarget.textContent = thisRetention ? "(" + this._formatAmount(thisRetention) + ")" : ""
    }
    if (this.hasPrRetentionBalanceTarget) {
      this.prRetentionBalanceTarget.textContent = retBalance ? this.currencyValue + this._formatAmount(retBalance) : ""
    }

    // Summary table: RETENTION TO BE PAID OUT row
    const retentionToPay = this.hasPrRetentionToPayTarget ? this._parseCurrency(this.prRetentionToPayTarget.value) : 0
    if (this.hasPrRetentionPaidBalanceTarget) {
      // Project 7976 (Phelipe 2026-10-07): its retention lands on a half cent,
      // and this cell used the unrounded balance while the total used the
      // rounded one, so it showed $(0.01) against a total that paid it all out.
      const heldRetention = this.retentionOneCentValue ? retBalance : prevRetention + thisRetentionRaw
      const paidBalance = Math.round((heldRetention - (prevRetentionToPay + retentionToPay)) * 100) / 100
      this.prRetentionPaidBalanceTarget.textContent = paidBalance ? this.currencyValue + this._formatAmount(paidBalance) : ""
      this.prRetentionPaidBalanceTarget.classList.toggle("text-red-600", paidBalance < 0)
    }

    // Adjustments
    const liquidatedDamages = this.hasPrLessLiquidatedDamagesTarget ? this._parseCurrency(this.prLessLiquidatedDamagesTarget.value) : 0
    const otherDeductions = this.hasPrOtherDeductionsTarget ? this._parseCurrency(this.prOtherDeductionsTarget.value) : 0
    const otherAdditions = this.hasPrOtherAdditionsTarget ? this._parseCurrency(this.prOtherAdditionsTarget.value) : 0
    const taxes = this.hasPrTaxesTarget ? this._parseCurrency(this.prTaxesTarget.value) : 0

    // Total payout = this_app - retention + retention_to_pay - liquidated - deductions + additions + taxes
    const netPayment = thisAppTotal - thisRetention + retentionToPay - liquidatedDamages - otherDeductions + otherAdditions + taxes

    if (this.hasPrNetPaymentTarget) {
      this.prNetPaymentTarget.textContent = this._formatAmount(netPayment)
    }
  }

  hideNewPrModal() {
    this._hideRenumberSelect()
    this.newPrModalTarget.classList.add("hidden")
    this._attachmentIds = []
    this._pendingUploads = 0
    if (this.hasPrFileListTarget) this.prFileListTarget.innerHTML = ""
    showIntercom()
  }

  stopPropagation(e) {
    e.stopPropagation()
  }

  // ── Renumber past PR (dropdown in the edit-past modal header) ──────

  _showRenumberSelect() {
    if (!this.hasRenumberWrapTarget || !this._editSequence) return
    const current = String(this._editSequence)
    const past = Array.from(this.element.querySelectorAll('.payment-request[data-is-past="true"]'))
      .map(r => r.dataset.sequence).filter(Boolean)
    if (!past.includes(current)) past.push(current)
    past.sort((a, b) => a - b)
    this.renumberSelectTarget.innerHTML = past
      .map(s => `<option value="${s}" ${s === current ? "selected" : ""}>#${s}</option>`).join("")
    this.renumberWrapTarget.classList.remove("hidden")
    this.renumberWrapTarget.classList.add("inline-flex")
  }

  _hideRenumberSelect() {
    if (!this.hasRenumberWrapTarget) return
    this.renumberWrapTarget.classList.add("hidden")
    this.renumberWrapTarget.classList.remove("inline-flex")
  }

  // After a past-PR edit saves, apply a number change (swap with the past
  // PR holding the chosen number). Reloads on success — everything renames.
  async _maybeSwapSequence() {
    if (!this.hasRenumberWrapTarget || this.renumberWrapTarget.classList.contains("hidden")) return false
    const target = this.renumberSelectTarget.value
    if (!target || String(target) === String(this._editSequence)) return false
    const resp = await this._ajax("POST", "swap_past_payment_request_sequence", {
      sequence: this._editSequence,
      target_sequence: target
    })
    if (!resp) return false
    sessionStorage.setItem("prFlashMessage", resp.message || "Updated successfully")
    window.location.reload()
    return true
  }

  // ── File upload ─────────────────────────────────────────────────

  dragoverDropzone(event) {
    event.preventDefault()
    this.prDropzoneTarget.classList.add("border-brand", "bg-blue-50/50")
  }

  dragleaveDropzone() {
    this.prDropzoneTarget.classList.remove("border-brand", "bg-blue-50/50")
  }

  dropDropzone(event) {
    event.preventDefault()
    this.prDropzoneTarget.classList.remove("border-brand", "bg-blue-50/50")
    const files = Array.from(event.dataTransfer.files).filter(prUploadable)
    if (files.length > 0) this._doFileUpload(files)
  }

  clickDropzone() {
    this.prFileInputTarget.click()
  }

  selectFiles(event) {
    const files = Array.from(event.target.files).filter(prUploadable)
    if (files.length > 0) this._doFileUpload(files)
    event.target.value = ""
  }

  // ── Pick from Photos ────────────────────────────────────────────
  // photo_picker_controller hands back the ticked photos; the server copies
  // each into a loose attachment so it rides this pay request like an
  // uploaded file (FinancesController#attach_project_photos).

  openPhotoPicker() {
    if (!this.hasPhotoPickerTarget) return
    this.application.getControllerForElementAndIdentifier(this.photoPickerTarget, "photo-picker")?.open()
  }

  async attachPickedPhotos(event) {
    const photos = event.detail?.photos || []
    if (photos.length === 0) return
    this._pendingUploads++
    this._updateUploadState()

    const placeholders = photos.map(p => {
      const chip = this._createFileChip(p.filename || `Photo #${p.id}`, { uploading: true })
      this.prFileListTarget.appendChild(chip)
      return chip
    })

    try {
      const res = await fetch(`/projects/${this.projectIdValue}/finances/attach_project_photos`, {
        method: "POST",
        headers: { "X-CSRF-Token": this._csrfToken, "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ photo_ids: photos.map(p => p.id) })
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const attached = await res.json()
      placeholders.forEach(p => p.remove())
      attached.forEach(a => this._addAttachmentChip(a))
    } catch (e) {
      console.error("Adding photos failed:", e)
      placeholders.forEach(p => p.remove())
      this._showToast(this.photoPickerTarget.dataset.errorText || "Couldn't add the photos", "alert")
    } finally {
      this._pendingUploads--
      this._updateUploadState()
    }
  }

  async _doFileUpload(files) {
    const token = this._csrfToken
    this._pendingUploads++
    this._updateUploadState()

    const placeholders = files.map(file => {
      const chip = this._createFileChip(file.name, { uploading: true })
      this.prFileListTarget.appendChild(chip)
      return chip
    })

    try {
      const presignUrl = `/projects/${this.projectIdValue}/finances/presign_uploads`
      const presignRes = await fetch(presignUrl, {
        method: "POST",
        headers: { "X-CSRF-Token": token, "Content-Type": "application/json" },
        body: JSON.stringify({
          files: files.map(f => ({
            filename: f.name,
            content_type: prContentType(f),
            byte_size: f.size
          }))
        })
      })

      if (!presignRes.ok) {
        const err = await presignRes.json().catch(() => ({}))
        throw new Error(err.error || "Failed to get upload URLs")
      }

      const { uploads } = await presignRes.json()

      const results = await Promise.allSettled(
        uploads.map(async (upload, i) => {
          const res = await fetch(upload.upload_url, {
            method: "PUT",
            headers: { "Content-Type": prContentType(files[i]) },
            body: files[i]
          })
          if (!res.ok) throw new Error(`S3 upload failed: ${res.status}`)
          return upload
        })
      )

      const successful = results.filter(r => r.status === "fulfilled").map(r => r.value)
      if (successful.length === 0) throw new Error("All uploads failed")

      const registerUrl = `/projects/${this.projectIdValue}/finances/register_uploads`
      const registerRes = await fetch(registerUrl, {
        method: "POST",
        headers: { "X-CSRF-Token": token, "Content-Type": "application/json" },
        body: JSON.stringify({
          files: successful.map(u => ({ cache_name: u.cache_name, filename: u.filename }))
        })
      })

      const registered = await registerRes.json()

      placeholders.forEach(p => p.remove())
      registered.forEach(a => this._addAttachmentChip(a))
    } catch (e) {
      console.error("File upload failed:", e)
      placeholders.forEach(p => p.remove())
      this._showToast("File upload failed", "alert")
    } finally {
      this._pendingUploads--
      this._updateUploadState()
    }
  }

  // A saved file on this pay request: its id goes out with the save, its chip
  // can be taken off again.
  _addAttachmentChip(a) {
    const name = a.filename || `File #${a.id}`
    this._attachmentIds.push(a.id)
    const chip = this._createFileChip(name, {
      removable: true,
      onRemove: () => {
        chip.remove()
        this._attachmentIds = this._attachmentIds.filter(id => id !== a.id)
      }
    })
    this.prFileListTarget.appendChild(chip)
  }

  _updateUploadState() {
    const uploading = this._pendingUploads > 0
    if (this.hasSaveDraftBtnTarget) this._setButtonUploading(this.saveDraftBtnTarget, uploading)
    if (this.hasSignRequestBtnTarget) this._setButtonUploading(this.signRequestBtnTarget, uploading)
    if (this.hasAddPastPrBtnTarget) this._setButtonUploading(this.addPastPrBtnTarget, uploading)
  }

  _setButtonUploading(btn, uploading) {
    btn.disabled = uploading
    btn.classList.toggle("opacity-60", uploading)
    btn.classList.toggle("cursor-not-allowed", uploading)
    btn.classList.toggle("pointer-events-none", uploading)
    if (uploading) {
      if (!btn.dataset.originalText) {
        btn.dataset.originalText = btn.textContent
        btn.style.minWidth = `${btn.offsetWidth}px`
      }
      btn.innerHTML = `<svg class="animate-spin h-4 w-4 inline-block align-middle mr-1 -mt-px" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path></svg>Uploading\u2026`
    } else if (btn.dataset.originalText) {
      btn.textContent = btn.dataset.originalText
      delete btn.dataset.originalText
      btn.style.minWidth = ""
    }
  }

  _createFileChip(name, { removable = false, uploading = false, onRemove = null, url = null } = {}) {
    const chip = document.createElement(url ? "a" : "div")
    if (url) {
      chip.href = url
      chip.target = "_blank"
      chip.rel = "noopener"
    }
    chip.className = `inline-flex items-center gap-1.5 pl-2 ${removable ? "pr-1" : "pr-2"} py-1 rounded-md text-xs${url ? " cursor-pointer hover:opacity-80" : ""}`
    chip.classList.add(uploading ? "chip-uploading" : "chip-file")

    const icon = uploading
      ? `<svg class="w-3.5 h-3.5 shrink-0 animate-spin" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path></svg>`
      : `<svg class="w-3.5 h-3.5 shrink-0 chip-file-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5"><path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"/></svg>`

    const truncatedName = name.length > 28 ? name.slice(0, 24) + "..." + name.slice(name.lastIndexOf(".")) : name

    let removeBtn = ""
    if (removable) {
      removeBtn = `<button type="button" class="ml-0.5 p-0.5 rounded hover:bg-gray-200 text-gray-400 hover:text-gray-600 transition-colors" data-remove-chip>
        <svg class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/></svg>
      </button>`
    }

    chip.innerHTML = `${icon}<span class="truncate max-w-[180px]" title="${this._escAttr(name)}">${truncatedName}</span>${removeBtn}`

    if (removable && onRemove) {
      const btn = chip.querySelector("[data-remove-chip]")
      btn.addEventListener("click", (e) => {
        e.stopPropagation()
        onRemove()
      })
    }

    return chip
  }

  // ── History Log ──────────────────────────────────────────────────

  _renderHistoryLog(entries) {
    if (!this.hasPrHistoryLogTarget) return
    this.prHistoryLogEntriesTarget.innerHTML = ""
    if (entries.length === 0) {
      this.prHistoryLogTarget.classList.add("hidden")
      return
    }
    entries.forEach(entry => {
      const p = document.createElement("p")
      p.className = "text-[12px] text-gray-400 leading-5"
      p.textContent = `${entry.datetime} - ${entry.username} (${entry.company_role}) ${entry.message}`
      this.prHistoryLogEntriesTarget.appendChild(p)
    })
    this.prHistoryLogTarget.classList.remove("hidden")
  }

  // ── Modal Header Badges ─────────────────────────────────────────

  _renderModalHeaderBadges(data) {
    const isEditing = !!this._editSequence
    const signedCount = data.signed_count || 0
    const tooltip = data.signature_tooltip || ""
    const showRequestBtn = data.show_request_approval_btn || false
    const isPast = data.is_past_payment || false
    const myRoleSigned = isEditing && data.my_role_signed
    const requiredSigs = data.required_sigs || 3

    this._currentRequiredSigs = requiredSigs

    // Request Approval button
    if (this.hasModalRequestApprovalBtnTarget) {
      if (isEditing && showRequestBtn && signedCount > 0) {
        this._pendingSignSequence = this._editSequence
        this.modalRequestApprovalBtnTarget.classList.remove("hidden")
        this.modalRequestApprovalBtnTarget.classList.add("inline-flex")
      } else {
        this.modalRequestApprovalBtnTarget.classList.add("hidden")
        this.modalRequestApprovalBtnTarget.classList.remove("inline-flex")
      }
    }

    // Signed badge
    if (this.hasModalSignedBadgeTarget) {
      if (isEditing && (signedCount > 0 || isPast)) {
        this.modalSignedTextTarget.textContent = isPast ? "Past Invoice" : `${signedCount}/${requiredSigs} Signed`
        this.modalSignedCheckTarget.classList.add("hidden")
        if (!isPast && tooltip) {
          this.modalSignedTooltipTarget.setAttribute("data-tip", tooltip)
          this.modalSignedTooltipTarget.setAttribute("data-tip-align", "left")
          this.modalSignedTooltipTarget.setAttribute("data-tip-pos", "below")
          this.modalSignedTooltipTarget.classList.remove("hidden")
        } else {
          this.modalSignedTooltipTarget.classList.add("hidden")
        }
        if (!isPast && myRoleSigned) this.modalSignedCheckTarget.classList.remove("hidden")
        const badge = this.modalSignedBadgeTarget
        badge.classList.remove("bg-green-100", "text-green-600", "bg-amber-100", "text-amber-600", "bg-gray-200", "text-gray-600")
        if (isPast) {
          badge.classList.add("bg-gray-200", "text-gray-600")
        } else if (signedCount < requiredSigs) {
          badge.classList.add("bg-amber-100", "text-amber-600")
        } else {
          badge.classList.add("bg-green-100", "text-green-600")
        }
        badge.classList.remove("hidden")
        badge.classList.add("inline-flex")
      } else {
        this.modalSignedBadgeTarget.classList.add("hidden")
        this.modalSignedBadgeTarget.classList.remove("inline-flex")
      }
    }

    // Owner-side approver: the owner, or a stakeholder granted pay-request
    // approval via PM Permissions (approve/reject/unsign only).
    const ownerApprover = this.isOwnerValue || this.canApproveValue

    // "Pending Contractor Signature" label — owners/consultants only, editing draft with 0 signatures
    if (this.hasModalPendingLabelTarget) {
      this.modalPendingLabelTarget.classList.toggle("hidden", !(
        (ownerApprover || this.isConsultantValue) && isEditing && signedCount === 0 && !isPast
      ))
    }

    // An owner who signed without the consultant: say who, where the pending label was.
    const skippedBy = isEditing ? data.consultant_skipped_by : null
    if (this.hasModalConsultantSkippedTarget) {
      const label = this.modalConsultantSkippedTarget
      label.textContent = skippedBy ? label.dataset.template.replace("%{who}", skippedBy) : ""
      label.classList.toggle("hidden", !skippedBy)
    }

    // "Sign without consultant" — owner side, contractor signed, consultant not yet
    if (this.hasModalSkipConsultantBtnTarget) {
      this.modalSkipConsultantBtnTarget.classList.toggle("hidden", !(isEditing && !isPast && canSignWithoutConsultant({
        allowed: this.skipConsultantAllowedValue, ownerApprover, myRoleSigned,
        contractorSigned: signedCount >= 1, consultantSigned: signedCount >= 2, requiredSigs
      })))
    }

    // "Pending Consultant Signature" label — owners/consultants only, contractor signed (1/3), consultant not waived
    if (this.hasModalPendingConsultantLabelTarget) {
      this.modalPendingConsultantLabelTarget.classList.toggle("hidden", !(
        (ownerApprover || this.isConsultantValue) && isEditing && signedCount === 1 && requiredSigs === 3 && !isPast
      ))
    }

    // Delete button — contractor on unsigned drafts, OR owner on past payments
    if (this.hasModalDeleteBtnTarget) {
      const showDelete = isEditing && (
        (this.isContractorValue && signedCount === 0 && !isPast) ||
        (this.isOwnerValue && isPast)
      )
      this.modalDeleteBtnTarget.classList.toggle("hidden", !showDelete)
    }

    // Edit Past PR button — past payments only, owner only
    if (this.hasModalEditPastBtnTarget) {
      this.modalEditPastBtnTarget.classList.toggle("hidden", !(isPast && this.isOwnerValue))
    }

    // Approve — consultant: visible if contractor signed AND not yet consultant-approved
    //         — owner: visible if contractor signed AND (consultant signed OR waived) AND not yet owner-approved
    const showApprove = isEditing && !myRoleSigned && !isPast && (
      (this.isConsultantValue && signedCount >= 1 && !skippedBy) ||
      (ownerApprover && signedCount >= (requiredSigs - 1))
    )
    if (this.hasModalApproveBtnTarget) this.modalApproveBtnTarget.classList.toggle("hidden", !showApprove)

    // Reject — owner/consultant when signedCount >= 1, but consultant can't reject once owner has signed
    const showReject = isEditing && signedCount >= 1 && !isPast && (
      ownerApprover ||
      (this.isConsultantValue && signedCount < requiredSigs)
    )
    if (this.hasModalRejectBtnTarget) this.modalRejectBtnTarget.classList.toggle("hidden", !showReject)

    // Sign + Save Draft — contractor only, unsigned, not past
    if (this.hasSignRequestBtnTarget) this.signRequestBtnTarget.classList.toggle("hidden", myRoleSigned || !this.isContractorValue || isPast)
    if (this.hasSaveDraftBtnTarget) this.saveDraftBtnTarget.classList.toggle("hidden", myRoleSigned || !this.isContractorValue || isPast)

    // Unsign — contractor clears all (only if not fully signed), consultant/owner only their own
    let showUnsign = false
    if (this.isContractorValue && myRoleSigned && signedCount < requiredSigs) {
      showUnsign = true
    } else if (ownerApprover && myRoleSigned && !isPast) {
      showUnsign = true
    } else if (this.isConsultantValue && myRoleSigned && signedCount < requiredSigs) {
      showUnsign = true
    }
    if (this.hasUnsignBtnTarget) this.unsignBtnTarget.classList.toggle("hidden", !showUnsign)

    // Lock fields when signed
    const locked = myRoleSigned || isPast || ((ownerApprover || this.isConsultantValue) && isEditing)
    this._setModalReadonly(locked)
  }

  _setModalReadonly(locked) {
    // Header fields
    const headerInputs = [
      this.hasPrProjectNumberTarget && this.prProjectNumberTarget,
      this.hasPrPoTarget && this.prPoTarget,
      this.hasPrVendorIdTarget && this.prVendorIdTarget,
      this.hasPrInvoiceNumberTarget && this.prInvoiceNumberTarget,
    ].filter(Boolean)
    headerInputs.forEach(input => {
      input.readOnly = locked
      const fp = input._flatpickr
      if (fp) {
        if (locked) {
          fp.set("clickOpens", false)
          fp.altInput.readOnly = true
          fp.altInput.classList.remove("!bg-yellow-50")
          fp.altInput.classList.add("bg-white")
        } else {
          fp.set("clickOpens", true)
          fp.altInput.readOnly = false
          fp.altInput.classList.remove("bg-white")
          fp.altInput.classList.add("!bg-yellow-50")
        }
      } else {
        if (locked) {
          input.classList.remove("bg-yellow-50", "!bg-yellow-50")
          input.classList.add("bg-white")
        } else {
          input.classList.remove("bg-white")
          input.classList.add("bg-yellow-50")
        }
      }
    })

    // Line item editable fields
    this.prLineItemsTarget.querySelectorAll("[data-field='this_application']").forEach(input => {
      input.readOnly = locked
      const td = input.closest("td")
      if (td) {
        td.classList.toggle("bg-yellow-50", !locked)
        td.classList.toggle("bg-white", locked)
        // Hide $ sign on empty amount cells when locked
        const sym = td.querySelector("[data-sym='this_application']")
        if (sym) {
          const hasValue = input.value.trim() !== ""
          sym.textContent = (!locked || hasValue) ? this.currencyValue : ""
        }
      }
    })

    // Retention & adjustments inputs
    const adjustmentInputs = [
      this.hasPrRetentionToPayTarget && this.prRetentionToPayTarget,
      this.hasPrLessLiquidatedDamagesTarget && this.prLessLiquidatedDamagesTarget,
      this.hasPrOtherDeductionsTarget && this.prOtherDeductionsTarget,
      this.hasPrOtherAdditionsTarget && this.prOtherAdditionsTarget,
      this.hasPrTaxesTarget && this.prTaxesTarget,
      this.hasPrContractorFeeTarget && this.prContractorFeeTarget,
    ].filter(Boolean)
    adjustmentInputs.forEach(input => {
      input.readOnly = locked
      const td = input.closest("td")
      if (td) {
        td.classList.toggle("bg-yellow-50", !locked)
        td.classList.toggle("bg-white", locked)
      }
    })

    // Dropzone — hide when locked
    if (this.hasPrDropzoneTarget) this.prDropzoneTarget.classList.toggle("hidden", locked)
    if (this.hasUploadLabelTarget) this.uploadLabelTarget.classList.toggle("hidden", locked)
    if (this.hasPrPickPhotosTarget) this.prPickPhotosTarget.classList.toggle("hidden", locked)

    // Hide remove buttons on file chips when locked
    if (this.hasPrFileListTarget) {
      this.prFileListTarget.querySelectorAll("[data-remove-chip]").forEach(btn => {
        btn.classList.toggle("hidden", locked)
      })
    }
  }

  // ── Validation ───────────────────────────────────────────────────

  _validatePr() {
    this._clearValidationErrors()

    // Invoice Number required (when field exists)
    if (this.hasPrInvoiceNumberTarget && !this.prInvoiceNumberTarget.value.trim()) {
      this._markFieldError(this.prInvoiceNumberTarget)
      this._showToast("Please enter an invoice number.", "alert")
      return false
    }

    // At least one amount entered anywhere in the yellow editable column —
    // line items, retention, or any of the summary adjustments (liquidated
    // damages, other deductions/additions, taxes).
    if (!this._hasAnyPrAmount()) {
      this._showToast("Please enter at least one amount.", "alert")
      return false
    }

    // No line item can exceed its available balance (negative balance)
    let hasOverage = false
    this.prLineItemsTarget.querySelectorAll("tr").forEach(row => {
      const contractCell = row.querySelector("[data-field='contract']")
      const coCell = row.querySelector("[data-field='co-totals']")
      const pcocCell = row.querySelector("[data-field='pcoc']")
      const contract = parseFloat(contractCell?.dataset.contract) || 0
      const coTotals = parseFloat(coCell?.dataset.coTotals) || 0
      const pcoc = parseFloat(pcocCell?.dataset.pcoc) || 0
      const aca = contract + coTotals + pcoc
      const prevPr = parseFloat(row.querySelector("[data-field='prev-pr']")?.dataset.prevTotal) || 0
      const thisApp = this._parseCurrency(row.querySelector("[data-field='this_application']")?.value)
      if (aca > 0 && Math.round((prevPr + thisApp - aca) * 100) / 100 > 0) hasOverage = true
    })
    if (hasOverage) {
      this._showToast("One or more line items have an amount above the available balance.", "alert")
      return false
    }

    // Retention to pay cannot exceed retention balance
    if (this.hasPrRetentionPaidBalanceTarget) {
      const paidBalanceText = this.prRetentionPaidBalanceTarget.textContent
      const paidBalance = this._parseCurrency(paidBalanceText)
      if (paidBalance < 0) {
        this._showToast("Retention to be paid out exceeds the available retention balance.", "alert")
        return false
      }
    }

    return true
  }


  _hasAnyPrAmount() {
    const lineItemFields = this.prLineItemsTarget.querySelectorAll("[data-field='this_application']")
    for (const el of lineItemFields) {
      if (this._parseCurrency(el.value) !== 0) return true
    }
    const summaryTargets = [
      this.hasPrRetentionToPayTarget && this.prRetentionToPayTarget,
      this.hasPrLessLiquidatedDamagesTarget && this.prLessLiquidatedDamagesTarget,
      this.hasPrOtherDeductionsTarget && this.prOtherDeductionsTarget,
      this.hasPrOtherAdditionsTarget && this.prOtherAdditionsTarget,
      this.hasPrTaxesTarget && this.prTaxesTarget
    ].filter(Boolean)
    return summaryTargets.some(el => this._parseCurrency(el.value) !== 0)
  }

  _markFieldError(input) {
    input.classList.add("!bg-red-100")
    input.addEventListener("input", () => input.classList.remove("!bg-red-100"), { once: true })
  }

  _clearValidationErrors() {
    this.newPrModalTarget.querySelectorAll(".\\!bg-red-100").forEach(el => {
      el.classList.remove("!bg-red-100")
    })
  }

  _allPreviousPrsSigned() {
    const cards = this.element.querySelectorAll(".payment-request[data-sequence]")
    let currentSeq = parseInt(this._editSequence) || 0
    if (!currentSeq) {
      // New PR — next sequence is max + 1, so all existing cards are "previous"
      cards.forEach(c => { currentSeq = Math.max(currentSeq, parseInt(c.dataset.sequence) || 0) })
      currentSeq += 1
    }
    for (const card of cards) {
      const seq = parseInt(card.dataset.sequence) || 0
      if (seq >= currentSeq) continue
      const isPast = card.dataset.isPast === "true"
      if (isPast) continue
      const signedCount = parseInt(card.dataset.signedCount) || 0
      const requiredSigs = parseInt(card.dataset.requiredSigs) || 3
      if (signedCount < requiredSigs) {
        this._showToast("All previous pending PRs must be fully signed before proceeding.", "alert")
        return false
      }
    }
    return true
  }

  // ── Save PR ────────────────────────────────────────────────────

  async saveDraft() { await this._submitPr(false) }

  async signAndRequest() {
    if (!this._allPreviousPrsSigned()) return
    if (!this._validatePr()) return
    if (this.requirePrAttachmentValue && this._attachmentIds.length === 0) {
      this._showToast("Please add at least one PDF or photo.", "alert")
      return
    }
    this._skipValidation = true
    await this._submitPr(true)
    this._skipValidation = false
  }

  async _submitPr(signAfter) {
    if (this._submitting) return
    if (!this._skipValidation && !this._validatePr()) return

    const lineItemsArr = this._collectPrLineItems()

    this._submitting = true
    this._disableSubmitButtons(true)

    const sequence = this._editSequence || await this._getNextPrSequence()
    const isUpdate = !!this._editSequence

    let action
    if (isUpdate) {
      action = "update_payment_request"
    } else {
      action = this._isPast ? "create_past_payment_request" : "create_payment_request"
    }

    const params = {
      sequence: sequence,
      line_items_arr: JSON.stringify(lineItemsArr),
      invoice_number: this.hasPrInvoiceNumberTarget ? this.prInvoiceNumberTarget.value : "",
      project_number: this.hasPrProjectNumberTarget ? this.prProjectNumberTarget.value : "",
      po_number: this.hasPrPoTarget ? this.prPoTarget.value : "",
      vendor_id: this.hasPrVendorIdTarget ? this.prVendorIdTarget.value : "",
      contractor_fee: this._getFeeAmount() || "",
      retention_rate: this._retainageRate || this.retainageValue || "",
      retention_to_pay: this.hasPrRetentionToPayTarget ? this._parseCurrency(this.prRetentionToPayTarget.value) || "" : "",
      less_liquidated_damages: this.hasPrLessLiquidatedDamagesTarget ? this._parseCurrency(this.prLessLiquidatedDamagesTarget.value) || "" : "",
      other_deductions: this.hasPrOtherDeductionsTarget ? this._parseCurrency(this.prOtherDeductionsTarget.value) || "" : "",
      other_additions: this.hasPrOtherAdditionsTarget ? this._parseCurrency(this.prOtherAdditionsTarget.value) || "" : "",
      taxes: this.hasPrTaxesTarget ? this._parseCurrency(this.prTaxesTarget.value) || "" : "",
      attachment_ids: this._attachmentIds.join(" ")
    }

    const method = isUpdate ? "PUT" : "POST"
    let saveResp
    try {
      saveResp = await this._ajax(method, action, params)
    } catch (e) {
      this._submitting = false
      this._disableSubmitButtons(false)
      throw e
    }

    if (signAfter && !this._isPast) {
      // Show signature acknowledge modal before signing
      this._pendingSignSequence = sequence
      this._signFromEditModal = true
      this._signFromEditIsNew = !isUpdate
      this._signFromEditSaveResp = saveResp
      this._submitting = false
      this._disableSubmitButtons(false)
      this.signModalTarget.classList.remove("hidden")
      return
    }

    // Update page card
    if (saveResp) {
      if (isUpdate) {
        if (this._isPast && await this._maybeSwapSequence()) return
        this._updatePageCard(saveResp)
      } else {
        this._insertNewPageCard(saveResp)
      }
      this._updateSummaryTable(saveResp.overall_summary)
    }

    this._submitting = false
    this._disableSubmitButtons(false)
    this.hideNewPrModal()
    this._showToast("Saved", "success")
    this._startPdfPolling(sequence, "PR")
  }

  _collectPrLineItems() {
    const items = []
    this.prLineItemsTarget.querySelectorAll("tr").forEach(row => {
      if (row.dataset.isFee === "true") return // fee is sent separately
      const sovLineItemId = row.dataset.sovLineItemId
      const amount = this._parseCurrency(row.querySelector("[data-field='this_application']")?.value)
      if (amount !== 0) {
        items.push([sovLineItemId, amount])
      }
    })
    return items
  }

  _getFeeAmount() {
    const feeRow = this.prLineItemsTarget.querySelector("tr[data-is-fee='true']")
    if (!feeRow) return 0
    return this._parseCurrency(feeRow.querySelector("[data-field='this_application']")?.value)
  }

  _disableSubmitButtons(disabled) {
    if (this.hasSaveDraftBtnTarget) this.saveDraftBtnTarget.disabled = disabled
    if (this.hasSignRequestBtnTarget) this.signRequestBtnTarget.disabled = disabled
  }

  async _getNextPrSequence() {
    const prElements = this.element.querySelectorAll(".payment-request[data-sequence]")
    let max = 0
    prElements.forEach(el => {
      const seq = parseInt(el.dataset.sequence)
      if (seq > max) max = seq
    })
    return max + 1
  }

  // ── Add Past Payment Request ────────────────────────────────────

  async addPastPaymentRequest() {
    if (!this._allPreviousPrsSigned()) return
    if (!this._validatePr()) return

    // Past PRs are historical records — always require the source PDF,
    // regardless of the company's require_pr_attachment setting.
    if (this._attachmentIds.length === 0) {
      this._showToast("Please add at least one PDF or photo.", "alert")
      return
    }

    // Show confirmation modal
    const nextSequence = await this._getNextPrSequence()
    if (this.hasPastPrSequenceLabelTarget) {
      this.pastPrSequenceLabelTarget.textContent = `PR#${nextSequence}`
    }
    this.pastPrConfirmModalTarget.classList.remove("hidden")
  }

  async confirmPastPr() {
    this.pastPrConfirmModalTarget.classList.add("hidden")
    this._isPast = true
    this._skipValidation = true
    await this._submitPr(false)
    this._skipValidation = false
  }

  hidePastPrConfirmModal() {
    this.pastPrConfirmModalTarget.classList.add("hidden")
  }

  // ── Delete ──────────────────────────────────────────────────────

  deleteFromModal() {
    const cards = this.element.querySelectorAll(".payment-request[data-sequence]")
    let maxSeq = 0
    cards.forEach(el => { const s = parseInt(el.dataset.sequence); if (s > maxSeq) maxSeq = s })
    if (parseInt(this._editSequence) !== maxSeq) {
      this._showToast("Must delete the most recent item first. To delete this item, please first delete any more recent records.", "alert")
      return
    }
    this._pendingDeleteSequence = this._editSequence
    this.deleteModalTarget.classList.remove("hidden")
  }

  hideDeleteModal() { this.deleteModalTarget.classList.add("hidden") }

  async confirmDelete() {
    const resp = await this._ajax("DELETE", "destroy_pr_line_items", { sequence: this._pendingDeleteSequence })
    this.hideDeleteModal()
    this.hideNewPrModal()
    this._removePageCard(this._pendingDeleteSequence)

    if (resp && resp.has_any_prs) {
      this._updateSummaryTable(resp.overall_summary)
    } else {
      if (this.hasOverallTotalsTarget) this.overallTotalsTarget.classList.add("hidden")
      if (this.hasEmptyStateTarget) this.emptyStateTarget.classList.remove("hidden")
    }
    this._showToast("Deleted", "success")
  }

  // ── Edit Past PR ──────────────────────────────────────────────

  editPastPr() {
    this._setModalReadonly(false)
    this._showRenumberSelect()
    if (this.hasModalEditPastBtnTarget) this.modalEditPastBtnTarget.classList.add("hidden")
    if (this.hasSaveDraftBtnTarget) {
      this.saveDraftBtnTarget.classList.remove("hidden")
      this.saveDraftBtnTarget.textContent = "Update Past PR"
    }
  }

  // ── Sign / Unsign ──────────────────────────────────────────────

  showSignModal(sequence) {
    this._pendingSignSequence = sequence
    this.signModalTarget.classList.remove("hidden")
  }

  hideSignModal() {
    setSkipConsultantMode(this, false)
    if (this.hasNtpSectionTarget) this.ntpSectionTarget.classList.add("hidden")
    if (this.hasNtpErrorTarget) this.ntpErrorTarget.classList.add("hidden")
    if (this._signFromEditModal || this._approveAfterAcknowledge) {
      this.signModalTarget.classList.add("hidden")
      this._signFromEditModal = false
      this._signFromEditIsNew = false
      this._signFromEditSaveResp = null
      this._approveAfterAcknowledge = false
      return
    }
    this.signModalTarget.classList.add("hidden")
  }

  async confirmSign(event) {
    // One request per gesture — key-repeat (held Enter) or double-clicks fire
    // this repeatedly while the request is still in flight.
    if (this._confirmInFlight) return
    this._confirmInFlight = true
    const confirmBtn = event?.currentTarget
    if (confirmBtn) confirmBtn.disabled = true
    try {
      await this._confirmSignInner()
    } finally {
      this._confirmInFlight = false
      if (confirmBtn) confirmBtn.disabled = false
    }
  }

  async _confirmSignInner() {
    // Validate NTP date if section is visible (before clearing flags)
    const ntpVisible = this.hasNtpSectionTarget && !this.ntpSectionTarget.classList.contains("hidden")
    let ntpDate = null
    if (ntpVisible) {
      ntpDate = this.hasNtpDateInputTarget ? this.ntpDateInputTarget.value : ""
      if (!ntpDate) {
        if (this.hasNtpErrorTarget) this.ntpErrorTarget.classList.remove("hidden")
        return
      }
    }

    const fromEditModal = this._signFromEditModal
    const isNew = this._signFromEditIsNew
    const fromApprove = this._approveAfterAcknowledge
    this._signFromEditModal = false
    this._signFromEditIsNew = false
    this._signFromEditSaveResp = null
    this._approveAfterAcknowledge = false

    let params = { sequence: this._pendingSignSequence }
    if (ntpDate) params.ntp_start_date = ntpDate
    params = withSkipConsultant(this, params)

    let resp = await this._ajax("POST", "sign_pr", params)
    if (this.hasNtpSectionTarget) this.ntpSectionTarget.classList.add("hidden")
    this.signModalTarget.classList.add("hidden")

    // A failed sign returns null; _ajax already surfaced the reason, so bail.
    if (!resp) return

    // Phase 6 — soft insurance gate: a non-compliant payee returns a
    // decision prompt instead of signing. Resolve it (owner: approve-anyway/
    // reject; contractor: heads-up) then continue with the real response.
    if (resp.requires_compliance_decision) {
      resp = await this._resolveComplianceDecision(resp, {
        signAction: "sign_pr", signParams: params,
        rejectAction: "reject_pr", rejectParams: { sequence: this._pendingSignSequence }
      })
      if (!resp) return
    }

    if (fromApprove) {
      if (resp) {
        this._updatePageCard(resp)
        this._updateModalState(resp)
        this._updateSummaryTable(resp.overall_summary)
      }
      this.hideNewPrModal()
      this._showToast("Approved", "success")
      this._startPdfPolling(this._pendingSignSequence, "PR")
      return
    }

    if (fromEditModal) {
      if (resp) {
        if (isNew) {
          this._insertNewPageCard(resp)
        } else {
          this._updatePageCard(resp)
        }
        this._updateSummaryTable(resp.overall_summary)
        this._updateModalState(resp)
      }

      // Show request approval modal for contractor after signing
      if (this.isContractorValue) {
        this._signedBeforeRequest = true
        this.requestApprovalModalTarget.classList.remove("hidden")
      } else {
        this.hideNewPrModal()
      }
      this._showToast("Signed", "success")
      this._startPdfPolling(this._pendingSignSequence, "PR")
      return
    }

    // Standalone sign from page card
    if (resp) {
      this._updatePageCard(resp)
      this._updateSummaryTable(resp.overall_summary)
    }
    this._showToast("Signed", "success")
    this._startPdfPolling(this._pendingSignSequence, "PR")
  }

  unsignFromModal() {
    this._pendingSignSequence = this._editSequence
    this.unsignModalTarget.classList.remove("hidden")
  }

  showUnsignModal(sequence) {
    this._pendingSignSequence = sequence
    this.unsignModalTarget.classList.remove("hidden")
  }

  hideUnsignModal() { this.unsignModalTarget.classList.add("hidden") }

  async confirmUnsign() {
    const resp = await this._ajax("POST", "unsign_pr", { sequence: this._pendingSignSequence })
    this.hideUnsignModal()
    if (resp) {
      this._updatePageCard(resp)
      this._updateModalState(resp)
      this._updateSummaryTable(resp.overall_summary)
    }
    this._showToast("Signature removed", "success")
    this._startPdfPolling(this._pendingSignSequence, "PR")
  }

  // ── Approve / Reject ────────────────────────────────────────────

  // The owner's Sign & Approve pop-up, in "without the consultant" mode.
  signWithoutConsultant() {
    this.approvePr()
    setSkipConsultantMode(this, true)
  }

  approvePr() {
    this._pendingSignSequence = this._editSequence
    this._approveAfterAcknowledge = true

    // Show NTP date section for owner on first PR or when NTP is empty
    if (this.isOwnerValue && this.hasNtpSectionTarget) {
      const seq = parseInt(this._editSequence)
      const startedAt = this.projectStartedAtValue
      if (seq === 1 || !startedAt) {
        this.ntpSectionTarget.classList.remove("hidden")
        if (this.hasNtpDateInputTarget) {
          this.ntpDateInputTarget.value = startedAt || ""
          // Trigger flatpickr to update display
          if (this.ntpDateInputTarget._flatpickr) {
            this.ntpDateInputTarget._flatpickr.setDate(startedAt || "", true)
          }
        }
        if (this.hasNtpErrorTarget) this.ntpErrorTarget.classList.add("hidden")
      }
    }

    this.signModalTarget.classList.remove("hidden")
  }

  showRejectPrModal() { this.rejectPrModalTarget.classList.remove("hidden") }
  hideRejectPrModal() {
    this.rejectPrModalTarget.classList.add("hidden")
    if (this.hasRejectCommentTarget) this.rejectCommentTarget.value = ""
  }

  async confirmRejectPr() {
    const comment = this.hasRejectCommentTarget ? this.rejectCommentTarget.value : ""
    const resp = await this._ajax("POST", "reject_pr", { sequence: this._editSequence, comment })
    this.hideRejectPrModal()
    if (resp) {
      this._updatePageCard(resp)
      this._updateModalState(resp)
      this._updateSummaryTable(resp.overall_summary)
    }
    this.hideNewPrModal()
    this._showToast("Rejected", "success")
    this._startPdfPolling(this._editSequence, "PR")
  }

  // ── Request approval ───────────────────────────────────────────

  showRequestApprovalModal() { this.requestApprovalModalTarget.classList.remove("hidden") }
  hideRequestApprovalModal() {
    this.requestApprovalModalTarget.classList.add("hidden")
    if (this._signedBeforeRequest) {
      this._signedBeforeRequest = false
      this.hideNewPrModal()
    }
  }

  async sendApprovalRequest() {
    const sections = this.requestApprovalModalTarget.querySelectorAll("[data-company-section]")
    for (const section of sections) {
      if (section.classList.contains("hidden")) continue
      const checked = section.querySelectorAll("input[name='approval_email']:checked")
      if (checked.length === 0) {
        this._showToast("Please select at least one person from each company.", "alert")
        return
      }
    }

    const checkboxes = this.requestApprovalModalTarget.querySelectorAll("input[name='approval_email']:checked")
    const emails = Array.from(checkboxes).map(cb => cb.dataset.email)
    if (emails.length === 0) return

    const resp = await this._ajax("POST", "request_pr_approval", {
      sequence: this._pendingSignSequence || this._editSequence,
      emails: JSON.stringify(emails)
    })
    if (resp) this._updatePageCard(resp)
    this._signedBeforeRequest = false
    this.requestApprovalModalTarget.classList.add("hidden")
    this.hideNewPrModal()
    this._showToast("Approval request sent", "success")
  }

  // ── General Info ───────────────────────────────────────────────

  showGeneralInfo() {
    this.generalInfoModalTarget.classList.remove("hidden")
    this._giCalcContractDays()
  }
  hideGeneralInfo() { this.generalInfoModalTarget.classList.add("hidden") }

  giStartDateChanged() {
    const start = this.giStartDateTarget.value
    const end = this.giEndDateTarget.value
    const days = parseInt(this.giContractDaysTarget.value, 10)
    if (start && end) {
      this._giCalcContractDays()
    } else if (start && days > 0) {
      this._giSetEndDateFromDays(start, days)
    }
  }

  giEndDateChanged() {
    const start = this.giStartDateTarget.value
    const end = this.giEndDateTarget.value
    if (start && end) {
      this._giCalcContractDays()
    }
  }

  giContractDaysChanged() {
    const start = this.giStartDateTarget.value
    const days = parseInt(this.giContractDaysTarget.value, 10)
    if (!days || days <= 0) return
    if (start) this._giSetEndDateFromDays(start, days)
  }

  _giCalcContractDays() {
    const start = this.giStartDateTarget.value
    const end = this.giEndDateTarget.value
    if (!start || !end) { this.giContractDaysTarget.value = ""; return }
    let diff = Math.round((new Date(end) - new Date(start)) / (1000 * 60 * 60 * 24))
    if (this.ownerCompanyIdValue === 549) diff += 1
    this.giContractDaysTarget.value = diff > 0 ? diff : ""
  }

  _giSetEndDateFromDays(startStr, days) {
    const date = new Date(startStr)
    const offset = this.ownerCompanyIdValue === 549 ? days - 1 : days
    date.setDate(date.getDate() + offset)
    const fp = this.giEndDateTarget._flatpickr
    if (fp) { fp.setDate(date, true) } else { this.giEndDateTarget.value = this._giFormatDate(date) }
  }

  _giFormatDate(date) {
    const y = date.getFullYear()
    const m = String(date.getMonth() + 1).padStart(2, "0")
    const d = String(date.getDate()).padStart(2, "0")
    return `${y}-${m}-${d}`
  }

  async saveGeneralInfo() {
    const params = {
      project_number: this.hasGiProjectNumberTarget ? this.giProjectNumberTarget.value : "",
      building_type: this.hasGiBuildingTypeTarget ? this.giBuildingTypeTarget.value : "",
      stake_mission: this.hasGiStakeMissionTarget ? this.giStakeMissionTarget.value : "",
      vendor_id: this.hasGiVendorIdTarget ? this.giVendorIdTarget.value : "",
      po_number: this.hasGiPoNumberTarget ? this.giPoNumberTarget.value : "",
      start_date: this.hasGiStartDateTarget ? this.giStartDateTarget.value : "",
      end_date: this.hasGiEndDateTarget ? this.giEndDateTarget.value : ""
    }

    if (this.hasGiContractorCompanyIdTarget) {
      params.contractor_company_id = this.giContractorCompanyIdTarget.value
    }

    await this._ajax("PUT", "save_pr_general_info", params)
    this.hideGeneralInfo()
    this._showToast("General info saved", "success")
  }

  // ── Page card DOM helpers ──────────────────────────────────────

  _updateSummaryTable(summary) {
    if (!summary) return
    if (this.hasSummaryOriginalContractTarget) this.summaryOriginalContractTarget.innerHTML = `<b>${this._formatAmount(summary.original_contract)}</b>`
    if (this.hasSummaryCoTotalsTarget) { this.summaryCoTotalsTarget.innerHTML = `<b class="${summary.co_totals < 0 ? "text-red-600" : ""}">${this._formatAmount(summary.co_totals)}</b>`; this.summaryCoTotalsTarget.classList.toggle("text-red-600", summary.co_totals < 0); if (this.hasSummaryCoSymbolTarget) { this.summaryCoSymbolTarget.classList.toggle("text-red-600", summary.co_totals < 0); this.summaryCoSymbolTarget.classList.toggle("text-gray-400", summary.co_totals >= 0) } }
    if (this.hasSummaryAcaTarget) this.summaryAcaTarget.innerHTML = `<b>${this._formatAmount(summary.aca)}</b>`
    if (this.hasSummaryCompletedTarget) this.summaryCompletedTarget.innerHTML = `<b>${this._formatAmount(summary.completed_to_date)}</b>`
    if (this.hasSummaryPercentTarget) this.summaryPercentTarget.textContent = summary.percent_completed
    if (this.hasSummaryBalanceTarget) this.summaryBalanceTarget.innerHTML = `<b>${this._formatAmount(summary.balance)}</b>`
    if (this.hasSummaryRetentionBalanceTarget) this.summaryRetentionBalanceTarget.innerHTML = `<b>${this._formatAmount(summary.retention_balance)}</b>`
    if (this.hasOverallTotalsTarget) this.overallTotalsTarget.classList.remove("hidden")
  }

  _buildBadgeHTML(data) {
    const signedCount = data.signed_count || 0
    const isPast = data.is_past_payment || false
    const tooltip = data.signature_tooltip || ""
    const myRoleSigned = data.my_role_signed || false
    const requiredSigs = data.required_sigs || 3

    if (isPast) {
      return `<span class="inline-flex items-center gap-1 bg-gray-200 text-gray-600 text-[12px] font-medium rounded-lg px-2.5 py-[2px]">Past Invoice</span>`
    }

    if (signedCount > 0) {
      const badgeBg = signedCount < requiredSigs ? "bg-amber-100 text-amber-600" : "bg-green-100 text-green-600"
      let iconHtml = (signedCount >= requiredSigs && myRoleSigned) ? SVG_CHECK : ""
      let tooltipHtml = tooltip ? `<span class="shrink-0 cursor-help flex items-center" data-tip="${this._escAttr(tooltip)}" data-tip-align="left" data-tip-pos="below">${SVG_INFO_CIRCLE}</span>` : ""
      return `<span class="inline-flex items-center gap-1 ${badgeBg} text-[12px] font-medium rounded-lg px-2.5 py-[2px]">${iconHtml} ${signedCount}/${requiredSigs} Signed ${tooltipHtml}</span>`
    }

    return `<span class="inline-flex items-center gap-1 bg-gray-200 text-gray-600 text-[12px] font-medium rounded-lg px-2.5 py-[2px]">DRAFT</span>`
  }

  _updatePageCard(data) {
    const card = this.element.querySelector(`.payment-request[data-sequence="${data.sequence}"]`)
    if (!card) return

    card.dataset.signedCount = data.signed_count || 0
    card.dataset.isPast = data.is_past_payment || false
    card.dataset.requiredSigs = data.required_sigs || 3

    // Badge
    const badgeArea = card.querySelector("[data-badge-area]")
    if (badgeArea) badgeArea.innerHTML = this._buildBadgeHTML(data)

    // Last updated
    const lastUpdated = card.querySelector("[data-last-updated]")
    if (lastUpdated && data.last_updated_at) {
      lastUpdated.textContent = `Last updated on ${data.last_updated_at} by ${data.who_updated || ""}`
    }

    // Total amount (net payment matching the modal)
    const totalEl = card.querySelector("[data-card-total-amount]")
    if (totalEl) {
      totalEl.textContent = `${this.currencyValue}${this._formatAmount(this._calcCardNetPayment(data))}`
    }
  }

  _buildFullCardHTML(data) {
    const seq = data.sequence
    const isPast = data.is_past_payment || false
    const requiredSigs = data.required_sigs || 3
    const pdfUrl = data.final_pdf_url || `/projects/${this.projectIdValue}/finances/pr_export_with_pdfs/${seq}.pdf`
    const total = this._calcCardNetPayment(data)
    const cur = this.currencyValue

    return `<div class="payment-request flex items-center justify-between px-3 py-2.5 text-[13px] text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer" data-sequence="${seq}" data-signed-count="${data.signed_count || 0}" data-is-past="${isPast}" data-required-sigs="${requiredSigs}" data-action="click->contractor-pay-request#editPaymentRequest">
      <div class="flex items-center gap-3">
        <span class="font-semibold text-gray-700 text-[14px]">Payment Request #${seq}</span>
        <span data-badge-area>${this._buildBadgeHTML(data)}</span>
      </div>
      <div class="flex items-center gap-3">
        <span data-export-area>
          <div class="relative" data-controller="dropdown">
            <button data-action="click->dropdown#toggle" class="inline-flex items-center gap-1 bg-gray-200 hover:bg-gray-300 text-gray-600 text-[12px] font-medium rounded-lg px-2.5 py-[2px] transition-colors cursor-pointer">Export <svg class="w-2.5 h-2.5" fill="currentColor" viewBox="0 0 10 6"><path d="M0 0l5 6 5-6z"/></svg></button>
            <div data-dropdown-target="menu" class="hidden absolute right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-30 min-w-[100px] py-1">
              <a href="${pdfUrl}" target="_blank" data-action="click->contractor-pay-request#stopPropagation" class="block px-3 py-1 text-[13px] text-gray-700 hover:bg-gray-50">PDF</a>
              <a href="/projects/${this.projectIdValue}/finances/pr_export/${seq}.xlsx" data-action="click->contractor-pay-request#stopPropagation" class="block px-3 py-1 text-[13px] text-gray-700 hover:bg-gray-50">Excel</a>
            </div>
          </div>
        </span>
        <span data-last-updated class="text-gray-400 text-[12px] italic">Last updated on ${data.last_updated_at || ""} by ${data.who_updated || ""}</span>
        <span data-card-total-amount class="font-semibold text-gray-700 text-[14px]">${cur}${this._formatAmount(total)}</span>
      </div>
    </div>`
  }

  _calcCardNetPayment(data) {
    const prTotal = parseFloat(data.pr_total) || 0
    const retRate = parseFloat(data.retention_rate) || 0
    const thisRetention = Math.round(prTotal * (retRate / 100) * 100) / 100
    const retentionToPay = parseFloat(data.retention_to_pay) || 0
    const liquidated = parseFloat(data.less_liquidated_damages) || 0
    const deductions = parseFloat(data.other_deductions) || 0
    const additions = parseFloat(data.other_additions) || 0
    const taxes = parseFloat(data.taxes) || 0
    return prTotal - thisRetention + retentionToPay - liquidated - deductions + additions + taxes
  }

  _updateBreadcrumbCount(delta) {
    const badge = document.getElementById("pr-breadcrumb-count")
    if (badge) {
      const current = parseInt(badge.textContent) || 0
      badge.textContent = Math.max(0, current + delta)
    }
  }

  _insertNewPageCard(data) {
    if (this.hasEmptyStateTarget) this.emptyStateTarget.classList.add("hidden")
    this._updateBreadcrumbCount(1)

    const cardsContainer = this.hasPrCardsTarget ? this.prCardsTarget : null
    if (!cardsContainer) return

    // Add container styling if first PR
    if (!cardsContainer.querySelector(".payment-request")) {
      cardsContainer.classList.add("flex", "flex-col", "gap-2")
    }

    const newCardHTML = this._buildFullCardHTML(data)
    const newSeq = data.sequence

    // Insert in descending sequence order
    const existingCards = cardsContainer.querySelectorAll(".payment-request[data-sequence]")
    let inserted = false
    for (const card of existingCards) {
      const cardSeq = parseInt(card.dataset.sequence)
      if (newSeq > cardSeq) {
        card.insertAdjacentHTML("beforebegin", newCardHTML)
        inserted = true
        break
      }
    }
    if (!inserted) {
      cardsContainer.insertAdjacentHTML("beforeend", newCardHTML)
    }
  }

  _removePageCard(sequence) {
    const card = this.element.querySelector(`.payment-request[data-sequence="${sequence}"]`)
    if (card) {
      card.remove()
      this._updateBreadcrumbCount(-1)
    }
  }

  _updateModalState(data) {
    this._renderModalHeaderBadges(data)
    this._renderHistoryLog(data.history_log || [])
  }

  // ── PDF polling ──────────────────────────────────────────────

  _startPdfPolling(sequence, docType) {
    if (this._pdfPollingTimers[sequence]) clearTimeout(this._pdfPollingTimers[sequence])
    this._setExportGenerating(sequence)

    const triggeredAt = Math.floor(Date.now() / 1000)
    const maxPollTime = 90000
    const poll = async () => {
      if (Date.now() - triggeredAt * 1000 > maxPollTime) {
        delete this._pdfPollingTimers[sequence]
        this._restoreExportButton(sequence, null)
        return
      }
      const data = await this._ajax("POST", "check_if_final_pdf_is_generating", { type: docType, sequence: sequence, triggered_at: triggeredAt })
      if (data && !data.generating) {
        delete this._pdfPollingTimers[sequence]
        this._restoreExportButton(sequence, data.pdf_url)
      } else {
        this._pdfPollingTimers[sequence] = setTimeout(poll, 3000)
      }
    }
    this._pdfPollingTimers[sequence] = setTimeout(poll, 3000)
  }

  _setExportGenerating(sequence) {
    const card = this.element.querySelector(`.payment-request[data-sequence="${sequence}"]`)
    if (!card) return
    const area = card.querySelector("[data-export-area]")
    if (!area) return
    area.innerHTML = `<span class="inline-flex items-center gap-1.5 text-amber-600 text-[12px] font-medium rounded-lg px-2.5 py-[2px]"><svg class="w-3 h-3 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>Generating PDF...</span>`
  }

  _restoreExportButton(sequence, pdfUrl) {
    const card = this.element.querySelector(`.payment-request[data-sequence="${sequence}"]`)
    if (!card) return
    const area = card.querySelector("[data-export-area]")
    if (!area) return
    const url = pdfUrl || `/projects/${this.projectIdValue}/finances/pr_export_with_pdfs/${sequence}.pdf`
    area.innerHTML = `<div class="relative" data-controller="dropdown">
      <button data-action="click->dropdown#toggle" class="inline-flex items-center gap-1 bg-gray-200 hover:bg-gray-300 text-gray-600 text-[12px] font-medium rounded-lg px-2.5 py-[2px] transition-colors cursor-pointer">Export <svg class="w-2.5 h-2.5" fill="currentColor" viewBox="0 0 10 6"><path d="M0 0l5 6 5-6z"/></svg></button>
      <div data-dropdown-target="menu" class="hidden absolute right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-30 min-w-[100px] py-1">
        <a href="${url}" target="_blank" data-action="click->contractor-pay-request#stopPropagation" class="block px-3 py-1 text-[13px] text-gray-700 hover:bg-gray-50">PDF</a>
        <a href="/projects/${this.projectIdValue}/finances/pr_export/${sequence}.xlsx" data-action="click->contractor-pay-request#stopPropagation" class="block px-3 py-1 text-[13px] text-gray-700 hover:bg-gray-50">Excel</a>
      </div>
    </div>`
  }

  // ── Toast ──────────────────────────────────────────────────────

  _showToast(message, type = "success") {
    const cssType = (type === "error" || type === "alert") ? "alert" : "notice"
    const icon = cssType === "notice"
      ? '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"/></svg>'
      : '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"/></svg>'
    const el = document.createElement("div")
    el.className = `flash-toast-${cssType}`
    el.style.zIndex = "9999"
    el.setAttribute("data-controller", "flash")
    el.setAttribute("data-flash-dismiss-after-value", "5000")
    el.setAttribute("role", "alert")
    const iconDiv = document.createElement("div")
    iconDiv.className = "shrink-0"
    iconDiv.innerHTML = icon
    const msgP = document.createElement("p")
    msgP.className = "flex-1 text-sm"
    msgP.textContent = message
    el.appendChild(iconDiv)
    el.appendChild(msgP)
    document.body.prepend(el)
  }

  // ── Phase 6: soft insurance gate decision ──────────────────────
  // The server returns { requires_compliance_decision, is_owner,
  // compliance_warning } when the payee's insurance isn't compliant. The
  // owner (final approver) chooses approve-anyway or reject; any other
  // signer gets an informational heads-up. Returns the real sign response
  // when the user proceeds, or null when they cancel/reject.
  async _resolveComplianceDecision(decision, { signAction, signParams, rejectAction, rejectParams }) {
    if (decision.is_owner) {
      const proceed = await confirmAction({
        title: "Insurance not compliant",
        message: `${decision.compliance_warning} You can approve this pay request anyway, or reject it.`,
        confirmLabel: "Approve anyway",
        cancelLabel: "Reject",
        danger: true
      })
      if (!proceed) {
        await this._ajax("POST", rejectAction, { ...rejectParams, comment: "Insurance not compliant" })
        this._showToast("Pay request rejected.", "alert")
        window.location.reload()
        return null
      }
    } else {
      const proceed = await confirmAction({
        title: "Insurance not up to date",
        message: `${decision.compliance_warning} Please update your coverage with the project owner. You can continue for now.`,
        confirmLabel: "Continue",
        cancelLabel: "Cancel"
      })
      if (!proceed) return null
    }
    return await this._ajax("POST", signAction, { ...signParams, acknowledge_compliance: true })
  }

  // ── AJAX helper ────────────────────────────────────────────────

  async _ajax(method, action, params = {}) {
    const url = `/projects/${this.projectIdValue}/finances/${action}`
    const body = new FormData()
    Object.entries(params).forEach(([k, v]) => {
      if (v !== "" && v !== null && v !== undefined) body.append(k, v)
    })

    const options = {
      method: method === "GET" ? "GET" : "POST",
      headers: { "X-CSRF-Token": this._csrfToken },
    }

    if (method !== "GET") {
      if (method !== "POST") body.append("_method", method.toLowerCase())
      options.body = body
    }

    try {
      const resp = await fetch(url, options)
      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}))
        this._showToast(data.error || "Request failed. Please try again.", "error")
        return null
      }
      if (resp.headers.get("content-type")?.includes("application/json")) {
        return await resp.json()
      }
      return true
    } catch (e) {
      console.error("PR request failed:", e)
      this._showToast("Request failed. Please try again.", "error")
      return null
    }
  }
}
